import { useNavigate } from '@tanstack/react-router';
import { Ban, GitMerge } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { RETENTION_REASON_LABELS, type CandidateDetail } from '@nest-recruit/shared';
import { Button, type ButtonSize } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { SearchInput, Textarea } from '../../../../components/Input';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { errorMessage } from '../../../../lib/api-client';
import { formatDate, formatIsoDate } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useCandidates, useDoNotRehire, useKeepCandidate } from '../../api/candidates-api';
import styles from './CandidatePanel.module.css';

// The profile's person-level actions — Merge with…, Do not rehire, Keep permanently — shared by
// the full profile's header and the pop-up's Details tab. Each opens beside its button (§5).

const MIN_REASON = 3;
const POPOVER_WIDTH = 340;
const MIN_QUERY = 2;
const SEARCH_RESULTS = 6;

/** D15 in words, from S30's rule — the same one the nightly sweep applies. */
export function consentText(candidate: CandidateDetail): string {
  return candidate.consentAt ? `Agreed to us keeping their details on ${formatDate(candidate.consentAt)}.` : 'No consent recorded.';
}

export function retentionText(candidate: CandidateDetail): string {
  const retention = candidate.retention;
  switch (retention.kind) {
    case 'live':
      return `Kept while their ${retention.liveApplications === 1 ? 'application is' : `${retention.liveApplications} applications are`} in progress. The countdown starts when every application has closed.`;
    case 'counting':
      return `${RETENTION_REASON_LABELS[retention.reason]}, so their details are due to be anonymised on ${formatIsoDate(retention.removalDueOn)} (${retention.periodDays} days), after that month's archive.`;
    case 'pooled':
      return `Kept while a talent pool holds them — until ${formatDate(retention.until)} unless their consent is renewed.`;
    case 'kept':
      return `Kept permanently${retention.setByName ? ` by ${retention.setByName}` : ''}${retention.setAt ? ` on ${formatDate(retention.setAt)}` : ''}${retention.reason ? `: ${retention.reason}` : ''}.`;
    case 'staff':
      return 'They joined, so their record is staff, never anonymised.';
    case 'anonymised':
      return 'Anonymised. Their identity and CV have been removed; scores and history remain.';
  }
}

/** S30 Q17 — the HR Lead keeps someone past retention, with a reason. */
export function KeepControl({ candidate }: { readonly candidate: CandidateDetail }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const keep = useKeepCandidate(candidate.id);
  const showToast = useToasts((state) => state.show);
  const [reason, setReason] = useState('');
  const isKept = candidate.retention.kind === 'kept';
  const run = (isKeeping: boolean): void => {
    close();
    keep.mutate(
      { keep: isKeeping, ...(isKeeping ? { reason: reason.trim() } : {}), version: candidate.version },
      {
        onSuccess: () => showToast({ tone: 'success', message: isKeeping ? `${candidate.fullName} is kept permanently` : `${candidate.fullName} follows the retention period again` }),
        onError: (error) => showToast({ tone: 'danger', message: "That didn't save", detail: errorMessage(error) }),
      },
    );
  };
  if (isKept) {
    return (
      <Button variant="outline" size="sm" isPending={keep.isPending} onClick={() => run(false)}>
        Stop keeping permanently
      </Button>
    );
  }
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" onClick={toggle} aria-haspopup="dialog">
        Keep permanently…
      </Button>
      <Popover anchor={anchor} onClose={close} label="Keep permanently">
        <form
          className={styles.keepForm}
          onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim().length >= MIN_REASON) run(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && reason.trim().length >= MIN_REASON) {
              event.preventDefault();
              run(true);
            }
          }}
        >
          <Field label="Why keep them past the retention period?" isRequired>
            {({ inputId }) => <Textarea id={inputId} rows={3} autoFocus maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />}
          </Field>
          <Button type="submit" variant="solid" size="sm" disabled={reason.trim().length < MIN_REASON}>
            Keep permanently
          </Button>
        </form>
      </Popover>
    </>
  );
}

/** F7.07 from the profile: pick the other person, then review the merge. */
export function MergeWithButton({ candidate, size = 'md' }: { readonly candidate: CandidateDetail; readonly size?: ButtonSize }): ReactNode {
  const { isOpen, anchor, triggerRef, open, close } = usePopover();
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const trimmed = query.trim();
  const results = useCandidates({ page: 1, pageSize: SEARCH_RESULTS + 1, ...(trimmed.length >= MIN_QUERY ? { query: trimmed } : {}) });
  const others = trimmed.length >= MIN_QUERY ? (results.data?.rows ?? []).filter((row) => row.id !== candidate.id).slice(0, SEARCH_RESULTS) : [];

  return (
    <>
      <Button ref={triggerRef} variant="outline" size={size} icon={GitMerge} onClick={open} aria-expanded={isOpen}>
        Merge with…
      </Button>
      {isOpen && (
        <Popover anchor={anchor} onClose={close} align="right" width={POPOVER_WIDTH} label="Merge with another record">
          <div className={styles.popoverBody}>
            <SearchInput value={query} placeholder="Name, email or mobile" autoFocus onChange={(event) => setQuery(event.target.value)} />
            {trimmed.length < MIN_QUERY && <p className={styles.quiet}>Find the other record for the same person.</p>}
            {trimmed.length >= MIN_QUERY && others.length === 0 && !results.isFetching && <p className={styles.quiet}>No one else matches.</p>}
          </div>
          {others.map((row) => (
            <MenuItem
              key={row.id}
              meta={[row.email, row.phone].filter(Boolean).join(' · ')}
              onClick={() => {
                close();
                void navigate({ to: '/candidates/merge', search: { a: candidate.id, b: row.id } });
              }}
            >
              {row.fullName}
            </MenuItem>
          ))}
        </Popover>
      )}
    </>
  );
}

/** F7.13 / S14 Q8 — HR Lead only, reason mandatory. */
export function DoNotRehireButton({ candidate, size = 'md', setLabel = 'Do not rehire' }: { readonly candidate: CandidateDetail; readonly size?: ButtonSize; readonly setLabel?: string }): ReactNode {
  const { isOpen, anchor, triggerRef, open, close } = usePopover();
  const [reason, setReason] = useState('');
  const change = useDoNotRehire(candidate.id);
  const show = useToasts((state) => state.show);
  const isSetting = !candidate.doNotRehire;
  const trimmed = reason.trim();
  const isReady = trimmed.length >= MIN_REASON;
  const label = isSetting ? setLabel : 'Clear do not rehire';

  async function submit(): Promise<void> {
    if (!isReady) return;
    close();
    setReason('');
    try {
      await change.mutateAsync({ isSetting, reason: trimmed, version: candidate.version });
      show({ tone: 'success', message: isSetting ? 'Marked do not rehire' : 'Do not rehire cleared' });
    } catch (cause) {
      show({ tone: 'danger', message: "Do not rehire wasn't changed", detail: errorMessage(cause) });
    }
  }

  return (
    <>
      <Button ref={triggerRef} variant={isSetting ? 'danger' : 'outline'} size={size} icon={Ban} onClick={open} aria-expanded={isOpen}>
        {label}
      </Button>
      {isOpen && (
        <Popover anchor={anchor} onClose={close} align="right" width={POPOVER_WIDTH} label={label}>
          <form
            className={styles.popoverBody}
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Field label={isSetting ? 'Why should this person not be hired again?' : 'Why clear it?'} hint="Only HR can see this reason." isRequired>
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  value={reason}
                  maxLength={1000}
                  rows={3}
                  autoFocus
                  onChange={(event) => setReason(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                      event.preventDefault();
                      void submit();
                    }
                  }}
                />
              )}
            </Field>
            <div className={styles.popoverActions}>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button variant={isSetting ? 'dangerSolid' : 'solid'} type="submit" disabled={!isReady}>
                {isSetting ? 'Do not rehire' : 'Clear do not rehire'}
              </Button>
            </div>
          </form>
        </Popover>
      )}
    </>
  );
}
